import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import creditoService from '../../services/creditoService';
import { useGlobalState } from '../../state';
import { toCurrency, formatShortDate } from '../../utils/formaters';

type Credito = any;

const getEstadoClasses = (estadoId?: number) => {
    switch (estadoId) {
        case 1: return 'bg-blue-100 text-blue-800 p-3 rounded';
        case 2: return 'bg-orange-100 text-orange-800 p-3 rounded';
        case 3: return 'bg-red-100 text-red-800 p-3 rounded';
        case 4: return 'bg-green-100 text-green-800 p-3 rounded';
        case 6: return 'bg-emerald-100 text-emerald-800 p-3 rounded';
        case 7: return 'bg-red-200 text-red-900 p-3 rounded';
        case 8:
        case 9:
        case 14: return 'bg-yellow-100 text-yellow-800 p-3 rounded';
        case 10:
        case 13: return 'bg-gray-100 text-gray-800 p-3 rounded';
        case 11: return 'bg-gray-800 text-white p-3 rounded';
        case 12: return 'bg-purple-100 text-purple-800 p-3 rounded';
        default: return 'bg-gray-100 text-gray-800 p-3 rounded';
    }
};

const DetalleCredito: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [credito, setCredito] = useState<Credito | undefined>(undefined);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(undefined);

      if (user === undefined) return; // waiting for auth
      if (!user) {
        navigate('/auth/signin');
        return;
      }

      if (!id || isNaN(Number(id))) {
        setError('Id de crédito inválido');
        setLoading(false);
        return;
      }

      try {
        const res: any = await creditoService.getCreditoById(Number(id));
        if (res?.success) {
          setCredito(res.data);
          try { localStorage.setItem('detalleCredito', JSON.stringify(res.data)); } catch {}
        } else {
          setError(res?.errors?.[0] ?? 'No se encontró el crédito');
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Ocurrió un error al obtener el crédito');
      } finally {
        setLoading(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const openAmortizacion = () => {
    if (id) navigate(`/prestamos/lista-cuotas/${id}`);
  };

  return (
    <DefaultLayout>
      <div className="p-6">
        <div className="max-w-6xl mx-auto bg-white dark:bg-boxdark rounded-xl p-6">
          <h1 className="text-2xl font-semibold mb-4">Detalle del Préstamo</h1>

          {loading && <Loading statusText="Cargando..." />}

          {error && <Alert type="failure" message={error} />}

          {!loading && credito && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">Estos son los datos de tu préstamo:</p>

              <div 
                className={`${getEstadoClasses(credito.estadoSolicitud?.id)} ${credito.estadoSolicitud?.explicacion ? 'cursor-help' : ''}`}
                title={credito.estadoSolicitud?.explicacion || credito.estadoSolicitud?.descripcion}
              >
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">Estado del préstamo <span className="font-bold">#{credito.id}</span></div>
                  <div className="text-sm font-bold flex items-center">
                    {credito.estadoSolicitud?.descripcion}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex justify-between">
                  <div className="text-title-sm font-semibold">{credito.lineaDeCredito?.nombre}</div>
                </div>

                

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between">
                  <div className="mt-2 text-sm text-gray-700">Tasa de interés anual (TNA): {credito.lineaDeCredito?.tasa?.valor + credito.lineaDeCredito?.tasa?.margenAdicional}%</div>
                  </div>
                </div>
              </div>

              <section>
                <h2 className="text-lg font-medium text-gray-700">Crédito</h2>
                <div className="bg-gray-50 p-4 rounded mt-2 space-y-3">
                  <div className="flex justify-between">
                    <div className="text-sm">Monto solicitado:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.monto)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Monto a recibir:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.montoNetoARecibir)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Número de cuotas:</div>
                    <div className="text-sm font-semibold">{credito.cantidadDeCuotas}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Valor cuota promedio:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.cuotaPromedio)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Fecha estimada de la primera cuota:</div>
                    <div className="text-sm font-semibold">{formatShortDate(credito.fechaPrimeraCuota)}</div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-medium text-gray-700">Gastos</h2>
                <div className="bg-gray-50 p-4 rounded mt-2 space-y-3">
                  <div className="flex justify-between">
                    <div className="text-sm">Gastos Generales:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.gastosPorcentaje)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Fondo de Reserva:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.gastosReserva)}</div>
                  </div>

                  <div className="flex justify-between">
                    <div className="text-sm">Total:</div>
                    <div className="text-sm font-semibold">{toCurrency(credito.gastos)}</div>
                  </div>
                </div>
              </section>

              <div className="text-center">
                <button onClick={openAmortizacion} className="text-title-xsm text-primario-celeste-800 hover:underline">
                  Ver tabla de amortización
                </button>
                          </div>

                          <div className="flex gap-3 justify-center mt-8">
                              <button
                                  onClick={() => navigate(-1)}
                                  className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                              >
                                  Volver
                              </button>
                          </div>
            </div>
          )}
        </div>
      </div>
    </DefaultLayout>
  );
};

export default DetalleCredito;